import * as signalR from '@microsoft/signalr';
import { SIGNALR_HUBS, FLEET_HUB_METHODS } from '../config/environment';
import { getAuthToken, getRefreshToken, ensureValidAccessToken, refreshAccessToken, logout } from './auth.service';

let connection = null;
let listeners = {};
let active = false;
let refreshing = false;
let reconnecting = false;
let authListenersSet = false;

const initAuthListeners = () => {
  if (authListenersSet) return;
  authListenersSet = true;

  window.addEventListener('auth:logout', () => {
    active = false;
    stopFleetHub();
  });

  /* Jeton yenilendiğinde AÇIK bağlantı koparılmaz: erişim jetonu yalnız el sıkışmada
     okunuyor (accessTokenFactory her bağlanmada güncel jetonu alır). Eskiden her
     yenileme tam bir stop/start turu tetikliyordu — yenileme yoğunlaştıkça bağlantı
     sürekli düşüyor, düşen bağlantı yeni yenileme doğuruyordu. Partner web de yalnız
     kopmuş bağlantıda yeniden bağlanıyor. */
  window.addEventListener('auth:token_refreshed', () => {
    if (!active) return;
    if (connection && connection.state === signalR.HubConnectionState.Connected) return;
    reconnectHub();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && active && connection?.state === signalR.HubConnectionState.Connected) {
      registerHub();
    }
  });
};

/* Hub 401 aldı. Yenileme auth.service'te tek-uçuşlu: axios interceptor'ı da aynı anda
   yeniliyorsa refresh jetonu iki kez yakılmaz. Oturumun bittiğine YALNIZ auth.service
   karar verir (fatal) ve çıkışı orası yayar — burada jeton silinmez, aksi hâlde ağ
   dalgalanması canlı oturumu düşürürdü. */
const handleAuthError = async () => {
  if (refreshing) return;
  refreshing = true;
  try {
    if (!getRefreshToken()) {
      logout();
      return;
    }

    const result = await refreshAccessToken();
    if (result?.success) await reconnectHub();
  } finally {
    refreshing = false;
  }
};

/* Tek seferde tek yeniden bağlanma: 'auth:token_refreshed' dinleyicisi ile
   handleAuthError aynı anda tetiklenebiliyor; korumasız iki stop/start turu
   birbirinin bağlantısını kapatırdı. */
const reconnectHub = async () => {
  if (!active || reconnecting) return;
  reconnecting = true;
  try {
    if (connection) {
      try { await connection.stop(); } catch {}
      connection = null;
    }
    try {
      await startFleetHub();
    } catch {}
  } finally {
    reconnecting = false;
  }
};

const buildConnection = () => {
  return new signalR.HubConnectionBuilder()
    .withUrl(SIGNALR_HUBS.FLEET, {
      accessTokenFactory: async () => {
        const result = await ensureValidAccessToken();
        return result?.accessToken || getAuthToken();
      },
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (ctx) => {
        if (!active) return null;
        if (ctx.previousRetryCount >= 10) return null;
        if (ctx.retryReason?.message?.includes('401')) {
          handleAuthError();
          return null;
        }
        const d = Math.min(1000 * Math.pow(2, ctx.previousRetryCount), 30000);
        return d + d * 0.2 * Math.random();
      }
    })
    .configureLogging(signalR.LogLevel.Warning)
    .build();
};

export const startFleetHub = async () => {
  initAuthListeners();
  active = true;

  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    return connection;
  }

  /* Jeton yoksa/bayatsa önce yenile — handleAuthError'a düşmek yerine. handleAuthError
     kendi içinde reconnectHub çağırdığı için, reconnectHub → startFleetHub zincirinden
     tetiklendiğinde 'reconnecting' kilidine takılıp bağlantı hiç kurulmazdı. */
  const ensured = await ensureValidAccessToken();
  const token = ensured?.accessToken || getAuthToken();

  if (!token) {
    if (!getRefreshToken()) logout();
    return null;
  }

  try {
    connection = buildConnection();

    connection.onreconnecting(() => {
      window.dispatchEvent(new CustomEvent('signalr:status', { detail: { status: 'reconnecting' } }));
    });

    connection.onreconnected(() => {
      window.dispatchEvent(new CustomEvent('signalr:status', { detail: { status: 'connected' } }));
      registerHub();
    });

    connection.onclose((err) => {
      window.dispatchEvent(new CustomEvent('signalr:status', { detail: { status: 'disconnected' } }));
      if (err?.message?.includes('401')) {
        handleAuthError();
      }
    });

    Object.entries(FLEET_HUB_METHODS).forEach(([key, method]) => {
      if (key === 'REGISTER') return;
      connection.on(method, (data) => {
        const eventName = `fleet:${method}`;
        window.dispatchEvent(new CustomEvent(eventName, { detail: data }));

        if (listeners[method]) {
          listeners[method].forEach((cb) => {
            try { cb(data); } catch (e) { console.error('SignalR listener error:', e); }
          });
        }
      });
    });

    await connection.start();
    window.dispatchEvent(new CustomEvent('signalr:status', { detail: { status: 'connected' } }));

    await registerHub();

    return connection;
  } catch (error) {
    if (error?.message?.includes('401')) {
      handleAuthError();
      return null;
    }
    console.error('SignalR connection failed:', error);
    window.dispatchEvent(new CustomEvent('signalr:status', { detail: { status: 'error', error } }));
    return null;
  }
};

const registerHub = async () => {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke(FLEET_HUB_METHODS.REGISTER);
      reportPresenceState(document.visibilityState === 'visible' ? 'active' : 'connected');
    } catch (e) {
      console.error('SignalR register failed:', e);
    }
  }
};

/**
 * Sekmenin ön planda olup olmadığını sunucuya bildirir (CRM canlı kullanıcı durumu ekranı).
 * Bağlantının açık olması kullanıcının ekrana baktığı anlamına gelmez.
 * Sessizce başarısız olur — presence bilgilendirme amaçlıdır.
 */
export const reportPresenceState = (state) => {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) return;
  connection.invoke('Heartbeat', state).catch(() => { /* yut */ });
};

// Sekme ön plana geçtiğinde/arka plana düştüğünde bildir. Tek kayıt: modül seviyesinde,
// bileşen ömründen bağımsız — bağlantı da modül seviyesinde yaşıyor.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    reportPresenceState(document.visibilityState === 'visible' ? 'active' : 'connected');
  });
}

export const stopFleetHub = async () => {
  active = false;
  if (connection) {
    try {
      await connection.stop();
    } catch (e) {
      console.error('SignalR stop error:', e);
    }
    connection = null;
    listeners = {};
  }
};

export const onFleetEvent = (method, callback) => {
  if (!listeners[method]) {
    listeners[method] = [];
  }
  listeners[method].push(callback);

  return () => {
    listeners[method] = listeners[method].filter((cb) => cb !== callback);
  };
};

export const getConnectionState = () => {
  if (!connection) return 'disconnected';
  return connection.state;
};
